// 공정 표준 참여 규칙 — 입력 검사 · 검토 마감·남은 날 · 새 판 항목 행 · 화면 행(인메모리·Supabase·운영자 서비스가 이 한 벌을 쓴다).

import { z } from "zod";

import { idSchema } from "../../../lib/uuid";
import { PROCESS_CREW_MAX, type ProcessStandardItem, type ProcessStandardVersion } from "../types";
import { AdminStandardConflictError, StandardItemMissingError } from "./errors";
import {
  ARTICLE_BODY_MAX,
  ARTICLE_TITLE_MAX,
  PROPOSAL_FIELDS,
  PROPOSAL_LIST_LIMIT,
  PROPOSAL_REASON_MAX,
  PROPOSAL_REVIEW_DAYS,
  VOTE_STANCES,
  type AdminProposalRow,
  type ArticleRevisionView,
  type NewItemInsert,
  type ProcessStandardArticleRevision,
  type ProcessStandardProposal,
  type ProcessStandardProposalVote,
  type ProposalDetailView,
  type ProposalField,
  type ProposalFilter,
  type ProposalView,
  type ProposalVoteView,
  type VoteStance,
} from "./types";

const DAY_MS = 24 * 60 * 60 * 1000;
export const OUTPUT_MAX = 9999;
export const CURING_MAX = 365;

export const reviewEndsAt = (createdAt: string): string => new Date(new Date(createdAt).getTime() + PROPOSAL_REVIEW_DAYS * DAY_MS).toISOString();

// 마감 전이면 검토 중 — DB 와 같이 now < review_ends_at.
export const isReviewOpen = (p: Pick<ProcessStandardProposal, "status" | "review_ends_at">, now: Date): boolean =>
  p.status === "reviewing" && now.getTime() < new Date(p.review_ends_at).getTime();

// 남은 날(올림) — 마감이 지났으면 0.
export const daysLeft = (reviewEnds: string, now: Date): number => Math.max(0, Math.ceil((new Date(reviewEnds).getTime() - now.getTime()) / DAY_MS));

export const fieldValueOf = (item: Pick<ProcessStandardItem, ProposalField>, field: ProposalField): number => Number(item[field]);

const decimals = (v: number, places: number): boolean => Math.abs(v * 10 ** places - Math.round(v * 10 ** places)) < 1e-9;

// 숫자 범위 — 인원 1~50 정수 · 작업량 0 초과 소수 둘째 자리 · 양생일 0 이상 0.5일 단위.
export const proposedValueError = (field: ProposalField, value: number): string | null => {
  if (!Number.isFinite(value)) return "숫자를 적어 주세요";
  if (field === "crew_size") {
    if (!Number.isInteger(value) || value < 1 || value > PROCESS_CREW_MAX) return `기능공 수는 1~${PROCESS_CREW_MAX}명 정수입니다`;
    return null;
  }
  if (field === "output_per_person_day") {
    if (value <= 0 || value > OUTPUT_MAX) return `1인 1일 작업량은 0 초과 ${OUTPUT_MAX} 이하입니다`;
    if (!decimals(value, 2)) return "1인 1일 작업량은 소수 둘째 자리까지입니다";
    return null;
  }
  if (value < 0 || value > CURING_MAX) return `양생일은 0~${CURING_MAX}일입니다`;
  if (!decimals(value * 2, 0)) return "양생일은 0.5일 단위입니다";
  return null;
};

const reasonSchema = (what: string) =>
  z.string().trim().min(1, `${what}를 적어 주세요`).max(PROPOSAL_REASON_MAX, `${what}는 ${PROPOSAL_REASON_MAX}자까지 넣을 수 있습니다`);

export const articleEditSchema = z.object({
  article_id: idSchema("시공법 글을 찾을 수 없습니다"),
  seen: z.object({ title: z.string(), body: z.string() }, { message: "새로고침한 뒤 다시 고쳐 주세요" }),
  title: z.string().trim().min(1, "제목을 적어 주세요").max(ARTICLE_TITLE_MAX, `제목은 ${ARTICLE_TITLE_MAX}자까지 넣을 수 있습니다`),
  body: z.string().max(ARTICLE_BODY_MAX, `본문은 ${ARTICLE_BODY_MAX}자까지 넣을 수 있습니다`),
});

export const proposalSchema = z
  .object({
    item_code: z.string().trim().min(1, "공정 표준 항목을 골라 주세요"),
    base_version_id: idSchema("공정 표준 판을 찾을 수 없습니다 — 새로고침해 주세요"),
    field: z.enum(PROPOSAL_FIELDS, { message: "바꿀 숫자를 골라 주세요" }),
    proposed_value: z.coerce.number({ message: "숫자를 적어 주세요" }),
    reason: reasonSchema("제안 이유"),
  })
  .superRefine((v, ctx) => {
    const message = proposedValueError(v.field, v.proposed_value);
    if (message) ctx.addIssue({ code: "custom", path: ["proposed_value"], message });
  });

export const voteSchema = z.object({
  proposal_id: idSchema("제안을 찾을 수 없습니다"),
  stance: z.enum(VOTE_STANCES, { message: "찬성·반대를 골라 주세요" }),
  reason: reasonSchema("의견 이유"),
});

// 목록 상한 — 없으면 null(전부) · 0·음수·NaN 은 0건 · 그 밖은 PROPOSAL_LIST_LIMIT 까지 정수로(인메모리·Supabase 한 벌).
export const proposalLimitOf = (filter: ProposalFilter): number | null => {
  if (filter.limit === undefined || filter.limit === null) return null;
  const n = Math.floor(filter.limit);
  return Number.isFinite(n) && n > 0 ? Math.min(n, PROPOSAL_LIST_LIMIT) : 0;
};

export const firstIssue = (error: z.ZodError): string => error.issues[0]?.message ?? "입력을 확인해 주세요";

// 다음 판 이름 — 기준 판 번호 + 1(번호 자체는 RPC 가 잠금 아래서 다시 정한다).
export const nextVersionLabel = (base: Pick<ProcessStandardVersion, "country" | "version">): string => `${base.country} ${base.version + 1}판`;

// 새 판 항목 — 기준 판 항목 전부를 새 id 로 복사하고 승인한 숫자만 바꾼다 · 같은 항목·같은 숫자 둘이면 거절.
export const buildNewVersionItems = (
  baseItems: readonly ProcessStandardItem[],
  approved: readonly Pick<ProcessStandardProposal, "item_code" | "field" | "proposed_value">[],
  makeId: () => string
): NewItemInsert[] => {
  const changes = new Map<string, Partial<Record<ProposalField, number>>>();
  for (const p of approved) {
    const byField = changes.get(p.item_code) ?? {};
    if (byField[p.field] !== undefined) throw new AdminStandardConflictError();
    byField[p.field] = Number(p.proposed_value);
    changes.set(p.item_code, byField);
  }
  const codes = new Set(baseItems.map((i) => i.code));
  for (const code of changes.keys()) if (!codes.has(code)) throw new StandardItemMissingError();
  return baseItems.map((i) => {
    const c = changes.get(i.code) ?? {};
    return {
      id: makeId(),
      code: i.code,
      phase_code: i.phase_code,
      name: i.name,
      unit: i.unit,
      crew_size: c.crew_size ?? i.crew_size,
      laborer_size: i.laborer_size,
      output_per_person_day: c.output_per_person_day ?? Number(i.output_per_person_day),
      curing_days: c.curing_days ?? Number(i.curing_days),
      source: i.source,
      sort_order: i.sort_order,
    };
  });
};

const voteViewOf = (v: ProcessStandardProposalVote, orgId: string | null): ProposalVoteView => ({
  id: v.id,
  org_name: v.org_name,
  stance: v.stance,
  reason: v.reason,
  created_at: v.created_at,
  is_mine: orgId !== null && v.org_id === orgId,
});

const countsOf = (votes: readonly ProcessStandardProposalVote[]): { pro: number; con: number } => ({
  pro: votes.filter((v) => v.stance === "pro").length,
  con: votes.filter((v) => v.stance === "con").length,
});

const byOldest = (a: { created_at: string }, b: { created_at: string }): number => a.created_at.localeCompare(b.created_at);

// 회원 화면 한 줄 — 다른 회사 org_id 는 내보내지 않는다.
export const proposalViewOf = (
  p: ProcessStandardProposal,
  votes: readonly ProcessStandardProposalVote[],
  orgId: string,
  itemNames: ReadonlyMap<string, string>,
  now: Date
): ProposalView => {
  const mine = votes.find((v) => v.org_id === orgId);
  return {
    id: p.id,
    item_code: p.item_code,
    item_name: itemNames.get(p.item_code) ?? null,
    base_version_id: p.base_version_id,
    field: p.field,
    current_value: Number(p.current_value),
    proposed_value: Number(p.proposed_value),
    reason: p.reason,
    org_name: p.org_name,
    is_mine: p.org_id === orgId,
    status: p.status,
    created_at: p.created_at,
    review_ends_at: p.review_ends_at,
    decided_at: p.decided_at,
    days_left: p.status === "reviewing" ? daysLeft(p.review_ends_at, now) : 0,
    review_open: isReviewOpen(p, now),
    ...countsOf(votes),
    my_vote: (mine?.stance as VoteStance | undefined) ?? null,
  };
};

export const proposalDetailOf = (
  p: ProcessStandardProposal,
  votes: readonly ProcessStandardProposalVote[],
  orgId: string,
  itemNames: ReadonlyMap<string, string>,
  now: Date
): ProposalDetailView => ({
  ...proposalViewOf(p, votes, orgId, itemNames, now),
  votes: [...votes].sort(byOldest).map((v) => voteViewOf(v, orgId)),
});

export const revisionViewOf = (r: ProcessStandardArticleRevision, orgId: string): ArticleRevisionView => ({
  id: r.id,
  org_name: r.org_name,
  is_mine: r.org_id === orgId,
  title_before: r.title_before,
  title_after: r.title_after,
  body_before: r.body_before,
  body_after: r.body_after,
  created_at: r.created_at,
});

// 운영자 화면 한 줄 — 회사 org_id 까지 보인다.
export const adminProposalRowOf = (
  p: ProcessStandardProposal,
  votes: readonly ProcessStandardProposalVote[],
  latestVersionId: string | null,
  itemNames: ReadonlyMap<string, string>,
  now: Date
): AdminProposalRow => ({
  id: p.id,
  item_code: p.item_code,
  item_name: itemNames.get(p.item_code) ?? null,
  base_version_id: p.base_version_id,
  base_is_latest: p.base_version_id === latestVersionId,
  field: p.field,
  current_value: Number(p.current_value),
  proposed_value: Number(p.proposed_value),
  reason: p.reason,
  org_id: p.org_id,
  org_name: p.org_name,
  status: p.status,
  created_at: p.created_at,
  review_ends_at: p.review_ends_at,
  review_open: isReviewOpen(p, now),
  ...countsOf(votes),
  votes: [...votes].sort(byOldest).map((v) => voteViewOf(v, null)),
});

// 숫자 열(numeric)을 숫자로 — Supabase 는 문자열로 줄 수 있다.
export const proposalOf = (row: ProcessStandardProposal): ProcessStandardProposal => ({
  ...row,
  current_value: Number(row.current_value),
  proposed_value: Number(row.proposed_value),
});
